import { z } from 'zod';
import { CalendarDate } from './project.js';
import { GoalDirection, GoalMetricKind, GoalPeriodKind } from './performance-goal.js';
import { OkrAmbition } from './okr-template.js';
/**
 * Gợi ý mục tiêu cá nhân — APM-PERF2-m, kế hoạch §6.7.
 *
 * ## ⚠️ AI chỉ gợi ý, người duyệt (luật #6)
 *
 * Output này **không** tự thành mục tiêu. Nó tới màn hình của quản lý trực
 * tiếp dưới dạng đề xuất, và chỉ khi họ bấm nhận thì `CreatePersonalGoalCommand`
 * mới chạy — kèm nguyên luật cặp đôi §6.4.
 *
 * ## Vì sao `rationale` bắt buộc, và vì sao nó phải nêu con số
 *
 * Người duyệt là một quản lý đang vội. Một đề xuất "đặt đúng hạn 90%" không có
 * căn cứ thì hoặc bị nhận bừa — và khi ấy AI đang đặt mục tiêu cho người khác
 * chứ không phải gợi ý — hoặc bị bỏ qua cả loạt. Buộc nêu mốc hiện tại làm cả
 * hai kết cục ấy khó xảy ra hơn.
 */
const SuggestedGoalCommon = {
    /** Cùng thang với cột `target_value`: tỉ lệ là 0–1, không phải 0–100. */
    targetValue: z.number().finite(),
    rationale: z.string().min(1).max(400),
};
/**
 * Một mục tiêu AI đề xuất — **union theo `source`**, đúng hình dạng của
 * `CreatePersonalGoalInput` (APM-OKR4-f).
 *
 * ## Vì sao phải có nhánh `manual`
 *
 * Cho tới APM-OKR4, skill chỉ đẻ được mục tiêu của danh mục sáu chỉ số. Nghĩa
 * là cả một loại mục tiêu chính đáng — "cải thiện danh tiếng", "bàn giao tri
 * thức", "rút ngắn một quy trình phiền toái" — không bao giờ được gợi ý, dù
 * người dùng viết thẳng nó trong yêu cầu. Objective nổi tiếng nhất trong bộ OKR
 * của một PM Google ở Blogger không có **một** chỉ số hệ thống nào.
 *
 * ## ⚠️ Không có hàng lai
 *
 * `auto` mang `metricKind` và không mang `title`/`unit`/`direction`; `manual`
 * thì ngược lại. `discriminatedUnion` cưỡng chế điều đó ngay ở `submit_result`,
 * nên model nhận lỗi ở đúng lượt nó gõ sai — thay vì lọt tới lệnh duyệt rồi bị
 * từ chối trước mặt người đang chờ.
 */
export const SuggestedGoal = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('auto'),
        metricKind: GoalMetricKind,
        /** Mốc hiện tại mà đề xuất dựa vào. `null` = chưa đo được chỉ số này. */
        currentValue: z.number().finite().nullable(),
        ...SuggestedGoalCommon,
    }),
    z.object({
        source: z.literal('manual'),
        title: z.string().trim().min(1).max(300),
        unit: z.string().trim().min(1).max(30),
        direction: GoalDirection,
        /**
         * Lấy số ở đâu — **bắt buộc**, và đây là cửa thay cho "phải có mốc đo".
         *
         * Mục tiêu tự do không ai đo hộ: job đêm không chạm tới nó. Một mục tiêu tự
         * do không nói được số lấy ở đâu là một thanh tiến độ đứng yên cả kỳ, và
         * người bị đo sẽ không biết đó là lỗi dữ liệu hay là kết quả của mình.
         */
        measuredBy: z.string().trim().min(1).max(200),
        /** Số hiện tại nếu người dùng đã nêu trong yêu cầu; `null` khi chưa ai đếm. */
        currentValue: z.number().finite().nullable().default(null),
        ...SuggestedGoalCommon,
    }),
]);
/**
 * Một Objective AI đề xuất, kèm các Key Result của nó — APM-OKR6-d,
 * [docs/plan-okr-objective.md](../../../docs/plan-okr-objective.md) §4.1.
 *
 * ## Vì sao hình dạng này, và vì sao nó không phải một thiết kế mới
 *
 * `OKR_TEMPLATE_SEED` (APM-OKR4) **đã** đúng hình dạng này: nó viết từ nguồn
 * Google, trước hệ thống. Cho tới APM-OKR6 thì thư viện dạy model viết Objective
 * + KR, model đọc, rồi hệ thống **vứt câu Objective đi** ở đúng bước lưu — ba
 * con số rơi vào một danh sách phẳng mà không còn gì nói chúng để làm gì.
 *
 * ## `ambition` ở tầng NÀY, không ở tầng Key Result
 *
 * "Cam kết" hay "khát vọng" là tính chất của một **ý định**, và hai loại chấm
 * bằng hai thước (Google OKR Playbook). Một Objective cam kết có Key Result
 * khát vọng là một câu không ai chấm được cuối kỳ.
 */
export const SuggestedObjective = z.object({
    /**
     * Câu định tính, **không mang số** — cưỡng chế cả ở đây lẫn ở `finalize`.
     *
     * ⚠️ `.trim()` **trước** `.min()`: Zod áp `.trim()` như một phép biến đổi sau
     * khi validate, nên thứ tự ngược sẽ cho một chuỗi toàn dấu cách đi lọt.
     */
    objective: z.string().trim().min(3).max(300),
    ambition: OkrAmbition,
    rationale: z.string().min(1).max(400),
    /**
     * Ít nhất **một** Key Result. Một Objective không KR là một câu khẩu hiệu:
     * cuối kỳ không có gì để chấm, và nó vẫn chiếm một dòng trong danh sách.
     */
    keyResults: z.array(SuggestedGoal).min(1).max(6),
});
export const SuggestPersonalGoalsOutput = z.object({
    /**
     * Tối đa **ba Objective**, và đó là một con số có chủ đích.
     *
     * Yêu cầu gốc: *"tôi sợ quá nhiều vừa khó theo dõi lại khó thực hiện"*. Một
     * người có bảy mục tiêu quý thì không có mục tiêu nào.
     *
     * ⚠️ Đếm **Objective** từ APM-OKR6-e, không đếm Key Result: trần cũ (3) đếm
     * nhầm tầng, nên nó thấp hơn Google (3–5 Objective) mà không ai nhận ra.
     */
    objectives: z.array(SuggestedObjective).max(3),
    /**
     * Vì sao danh sách ngắn hoặc rỗng.
     *
     * Rỗng là một câu trả lời **đúng** — người chưa đủ dữ liệu thì mọi mức đích
     * đều là số bịa. Bắt buộc phải có ít nhất một gợi ý là mời model dựng một
     * cái để có thứ điền vào.
     */
    note: z.string().max(600).nullable(),
    /**
     * Loại kỳ mà gợi ý này nhắm tới — APM-OKR3, người dùng yêu cầu 2026-09-22.
     *
     * ## ⚠️ Model **không** đặt trường này, `finalize()` đặt
     *
     * Nó tới từ tab người dùng đang đứng (`SuggestGoalsInput.periodKind`), nên nó
     * là một **sự thật của yêu cầu**, không phải một phán đoán. Để model điền là
     * mời nó nói khác đi, rồi một gợi ý xin cho tháng sẽ nằm ở tab Năm.
     *
     * Model vẫn được **biết** kỳ nào (xem `buildUserMessage`): mức đích của một
     * tháng và của một năm khác nhau cả một bậc, nên không nói ra là mời nó đoán.
     *
     * `optional` chỉ vì những hàng ghi **trước** 2026-09-22 không có trường này.
     * Chúng hiện ở tab Quý — đúng thứ chúng vốn là, vì cho tới hôm ấy skill chỉ
     * biết một loại kỳ. Xem `pendingSuggestions`.
     */
    periodKind: GoalPeriodKind.optional(),
});
/** Tên skill lưu ở `ai_suggestion.skill` — dùng chung FE↔BE để lọc. */
export const SUGGEST_PERSONAL_GOALS_SKILL = 'suggest_personal_goals';
/**
 * Một gợi ý đang chờ duyệt, đã ghép sẵn để giao diện vẽ — APM-PERF2-n.
 *
 * Trả `goals` đã parse thay vì `content` thô: màn hình duyệt cần vẽ từng dòng
 * kèm ô chọn, và bắt nó tự parse JSON là để một gợi ý sinh bởi phiên bản cũ
 * làm vỡ cả trang.
 */
export const GoalSuggestionView = z.object({
    id: z.uuid(),
    ownerId: z.uuid(),
    objectives: z.array(SuggestedObjective),
    note: z.string().nullable(),
    rationale: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /**
     * Tab nào được hiện gợi ý này — APM-OKR3, người dùng yêu cầu 2026-09-22:
     * *"gợi ý OKR cho OKR tháng riêng, quý riêng và Năm riêng"*.
     *
     * Không có trường này thì một gợi ý xin cho tháng hiện ở cả ba tab, và mức
     * đích của một tháng bấm nhận vào kỳ năm là một cái đích thấp gấp mười hai
     * lần — không lỗi nào báo, vì mọi con số đều hợp lệ.
     */
    periodKind: GoalPeriodKind,
});
/**
 * Duyệt **một phần** gợi ý — ADR-005: người bấm chọn từng dòng, không nhận cả lô.
 *
 * ⚠️ Rỗng là không hợp lệ. "Duyệt không mục nào" là **bỏ qua**, và bỏ qua có
 * đường riêng kèm lý do (spec 12.1 dùng lý do ấy để cải thiện gợi ý). Cho phép
 * mảng rỗng ở đây là đẻ ra một cách đóng gợi ý không để lại lý do nào.
 */
/** Bối cảnh người hỏi muốn model cân nhắc. Bỏ trống thì dùng câu mặc định. */
export const SuggestGoalsInput = z.object({
    prompt: z.string().trim().max(2_000).optional(),
    /**
     * Kỳ mà người hỏi đang đứng — **bắt buộc**, người dùng yêu cầu 2026-09-22.
     *
     * Không có mặc định: mỗi tab (Tháng · Quý · Năm) có nút gợi ý của riêng nó,
     * nên chỗ gọi luôn biết. Một mặc định `'quarter'` ở đây chỉ để đỡ phải truyền
     * sẽ làm nút ở tab Tháng xin gợi ý cho quý — im lặng, và mức đích sai gấp ba.
     */
    periodKind: GoalPeriodKind,
    /**
     * Mã dấu vết do **client** sinh — APM-1302, cùng ngữ nghĩa với
     * `RunSkillInput.runId`.
     *
     * Endpoint này đứng ngoài đường chung `POST /ai/skills/:name/run` (mục tiêu
     * quý không thuộc dự án nào — xem `PersonalGoalController.suggest`), nên nó
     * phải tự mang mã theo. Thiếu nó thì lượt chạy vẫn xong và gợi ý vẫn về,
     * nhưng bảng "AI đang làm gì" ở cạnh phải trống trơn — người dùng bấm một nút
     * có trả tiền rồi nhìn một nút xám suốt mười lăm giây mà không biết nó sống
     * hay chết.
     */
    runId: z.uuid().optional(),
});
/**
 * Một Key Result được tick, định vị bằng **cặp chỉ số** — APM-OKR6-d.
 *
 * Không phải một chỉ số phẳng: cùng một `metricKind` xuất hiện được ở hai
 * Objective khác nhau, và một chỉ số phẳng sẽ gắn nó vào Objective sai.
 */
export const AcceptedKeyResult = z.object({
    objectiveIndex: z.number().int().nonnegative(),
    keyResultIndex: z.number().int().nonnegative(),
});
export const AcceptGoalSuggestionInput = z.object({
    /**
     * Những Key Result người duyệt tick — APM-OKR6-d.
     *
     * ⚠️ Objective được tạo **theo** danh sách này, không phải song song với nó:
     * một Objective mà người duyệt bỏ hết KR thì không được tạo (§6 mục 3). Một
     * Objective rỗng là đúng thứ `SuggestedObjective.keyResults.min(1)` ngăn ở
     * đầu kia.
     */
    keyResults: z.array(AcceptedKeyResult).min(1).max(18),
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    /**
     * Loại kỳ — APM-OKR2-b.
     *
     * ⚠️ Không suy ra từ hai ngày: một khoảng ba tháng có thể là một quý hoặc
     * ba tháng gộp, và đoán sai làm mục tiêu biến mất khỏi đúng tab người dùng
     * đang tìm nó. Người bấm nhận đang đứng ở một tab cụ thể, nên họ biết.
     */
    periodKind: GoalPeriodKind,
});
export const DismissGoalSuggestionInput = z.object({
    /**
     * Bắt buộc — spec 12.1: đây là dữ liệu để gợi ý lần sau đỡ sai hơn.
     *
     * ⚠️ `.trim()` đứng **trước** `.min(1)`, không phải sau.
     *
     * Zod áp `.trim()` như một phép biến đổi **sau** khi validate, nên
     * `.min(1).trim()` cho một chuỗi toàn dấu cách đi lọt rồi mới biến nó thành
     * chuỗi rỗng — tức một ô "bắt buộc" nhận được khoảng trắng. Bắt được nhờ e2e
     * gửi `'  '`.
     */
    reason: z.string().trim().min(1).max(500),
});
//# sourceMappingURL=ai-performance-goal.js.map